import type { BoggleBadgeFamilyId } from './familyTypes'

/**
 * Authored Badge metadata. Definitions are pure data; evaluation lives in
 * `~/boggle/lib/badges/`. New badges should be added under
 * `~/boggle/config/badges/definitions/` and wired into `registry.ts`.
 */
export type BoggleBadgeId =
  | 'abecedarian1'
  | 'abecedarian2'
  | 'abecedarian3'
  | 'anadrome1'
  | 'anadrome2'
  | 'battery1'
  | 'battery2'
  | 'battery3'
  | 'cartographer1'
  | 'cartographer2'
  | 'cartographer3'
  | 'cartographer4'
  | 'clutch1'
  | 'clutch2'
  | 'clutch3'
  | 'cornered'
  | 'doubleDouble'
  | 'explorer'
  | 'frenzy1'
  | 'frenzy2'
  | 'frenzy3'
  | 'generalist1'
  | 'generalist2'
  | 'generalist3'
  | 'greedy1'
  | 'isogram'
  | 'juggernaut1'
  | 'juggernaut2'
  | 'juggernaut3'
  | 'juggernaut4'
  | 'lexicographer1'
  | 'lexicographer2'
  | 'lexicographer3'
  | 'lexicographer4'
  | 'longStart'
  | 'luckyCharm'
  | 'machine1'
  | 'machine2'
  | 'machine3'
  | 'master'
  | 'palindrome'
  | 'pangrammatist1'
  | 'pangrammatist2'
  | 'pangrammatist3'
  | 'pangrammatist4'
  | 'pizazz1'
  | 'pizazz2'
  | 'prospector1'
  | 'prospector2'
  | 'prospector3'
  | 'quickdraw1'
  | 'quickdraw2'
  | 'quickdraw3'
  | 'sesquipedalian1'
  | 'sesquipedalian2'
  | 'sesquipedalian3'
  | 'sorcerer'
  | 'spellingBee'
  | 'square'
  | 'steady1'
  | 'steady2'
  | 'steady3'
  | 'steady4'
  | 'straight'
  | 'straightForward'
  | 'strongStart'
  | 'tartarSauce'
  | 'verbose'
  | 'versatile1'
  | 'versatile2'
  | 'versatile3'
  | 'versatile4'

export type BoggleBadgeCategory =
  | 'length'
  | 'letters'
  | 'mastery'
  | 'path'
  | 'special'
  | 'speed'

export type BoggleBadgeLevel =
  | 'common'
  | 'impossible'
  | 'legendary'
  | 'rare'
  | 'uncommon'

/**
 * Locked-tooltip reveal policy. `reveal` shows the description in the
 * Compendium even before earning; `mystery` shows `?` until earned; `hidden`
 * omits the badge from the Compendium until earned (dev mode may reveal it).
 * Future prerequisite-gated reveals can be layered on without breaking this field.
 */
export type BoggleBadgeLockedReveal = 'hidden' | 'mystery' | 'reveal'

/** Points awarded per badge rarity (Common → Impossible). */
export const BOGGLE_BADGE_POINTS_BY_LEVEL: Record<BoggleBadgeLevel, number> = {
  common: 2,
  impossible: 10,
  legendary: 8,
  rare: 6,
  uncommon: 4,
}

export function getBoggleBadgePointsForLevel(level: BoggleBadgeLevel): number {
  return BOGGLE_BADGE_POINTS_BY_LEVEL[level]
}

export type BoggleBadgeDefinition = {
  category: BoggleBadgeCategory
  /** How the badge is earned. Surfaced in the Compendium (subject to `lockedReveal`). */
  description: string
  /** Bracket template for tiered families; omitted on flat badges. */
  descriptionTemplate?: string
  /** Present when this definition is one tier of a tiered family. */
  familyId?: BoggleBadgeFamilyId
  /** Stable id used as a placeholder for future art/icon work. */
  iconId: string
  id: BoggleBadgeId
  level: BoggleBadgeLevel
  /** Authored locked-state behavior in the Compendium. */
  lockedReveal: BoggleBadgeLockedReveal
  name: string
  /** Authored point award from rarity (`getBoggleBadgePointsForLevel`). */
  points: number
  /** 1-based tier within `familyId`; omitted on flat badges. */
  tierIndex?: number
}
